import React, { useEffect, useState } from "react";
import { Copy, Share2, TrendingUp, Award, Crown, DollarSign, Link as LinkIcon, Mail, AlertTriangle, BookOpen, Sparkles, Check, ArrowRight, Banknote, Zap, Calendar, Settings, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import ConnectOnboardingModal from "../components/ConnectOnboardingModal";
import ConnectInlinePanels from "../components/ConnectInlinePanels";

const SWIPE_FILES = {
  twitter: `I just shipped a year of viral Substack content in 30 minutes.

Voice interview → AI engine → 52 posts + native versions for every platform.

If you run brands (yours or clients), this is the move:
{link}

40% of every customer who signs up forever lands in my account. Open invite.`,
  email_warm: `Subject: I've been beta-testing this and you have to see it

Hey — quick one.

You know how I've been talking about wanting a real, automated Substack engine?

I found it. It does the year of content, the cover art, every platform, scheduling — even autopilot publishing. I went from zero to a full publication calendar in an afternoon.

For your {brand_count} brands you'd 10x faster than us.

My link (it's a referral, full transparency): {link}

— {your_name}`,
  linkedin: `For the operators running 3+ brands (clients or your own):

This is the only tool that actually does the year-of-content promise.

10 min interview → 52 viral-tuned Substack posts → adapted to 13 platforms → autopilot ship.

I'm running it on {brand_count} brands. It's saved me ~40 hrs/week.

Comment "engine" for the link, or click here: {link}`,
  dm: `Yo — saw your post about wanting to publish more. The tool I've been using is Vitality — it literally writes a year of Substack content + adapts it to every platform. Saved me from quitting. Here's my link (gets you a free trial): {link}`,
};

export default function Affiliate() {
  const [me, setMe] = useState(null);
  const [refs, setRefs] = useState([]);
  const [credits, setCredits] = useState([]);
  const [working, setWorking] = useState(false);
  const [showSwipes, setShowSwipes] = useState(true);
  const [activeSwipe, setActiveSwipe] = useState("twitter");
  const [payoutEmail, setPayoutEmail] = useState("");
  const [connectStatus, setConnectStatus] = useState(null);
  const [showConnect, setShowConnect] = useState(false);
  const [pref, setPref] = useState("monthly");
  const [threshold, setThreshold] = useState(10000);

  const load = async () => {
    const m = await api.affiliateMe();
    setMe(m);
    setPayoutEmail(m.payout_email || "");
    setPref(m.auto_payout_preference || "monthly");
    setThreshold(m.auto_payout_threshold_cents || 10000);
    setRefs(await api.affiliateReferrals());
    setCredits(await api.affiliateCredits());
    try { setConnectStatus(await api.connectStatus()); } catch (_) { setConnectStatus({connected: false}); }
  };

  useEffect(() => { load(); }, []);

  const copyLink = () => {
    navigator.clipboard.writeText(me.public_link);
    toast.success("Link copied — paste it everywhere.");
  };

  const copySwipe = (key) => {
    let txt = SWIPE_FILES[key];
    txt = txt.replaceAll("{link}", me.public_link)
             .replaceAll("{brand_count}", me.active_referrals > 0 ? `${me.active_referrals + 1}` : "multiple")
             .replaceAll("{your_name}", (me.name || me.email || "").split(" ")[0] || "");
    navigator.clipboard.writeText(txt);
    toast.success("Swipe copied. Paste & ship.");
  };

  const savePayout = async () => {
    if (!payoutEmail.includes("@")) { toast.error("Valid email required"); return; }
    setWorking(true);
    try {
      await api.updateAffiliate(payoutEmail);
      toast.success("Payout email saved.");
      await load();
    } finally { setWorking(false); }
  };

  const requestPayout = async () => {
    const isConnect = connectStatus?.payouts_enabled;
    const dest = isConnect ? "your Stripe-verified bank account" : me.payout_email;
    if (!confirm(`Request payout of $${((me.pending_payout_cents || 0) / 100).toFixed(2)} to ${dest}?`)) return;
    setWorking(true);
    try {
      const r = await api.requestPayout();
      toast.success(r.method === "stripe_connect"
        ? "Sent! Money lands in your bank within 1-2 business days."
        : "Payout requested. We'll send within 7 business days.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Payout failed");
    } finally { setWorking(false); }
  };

  const savePrefs = async (nextPref, nextThreshold) => {
    setWorking(true);
    try {
      await api.updatePayoutPrefs(nextPref || pref, nextThreshold ?? threshold);
      toast.success("Payout preferences saved.");
      await load();
    } catch (e) {
      toast.error("Could not save preferences");
    } finally { setWorking(false); }
  };

  const disconnectStripe = async () => {
    if (!confirm("Disconnect Stripe? You can reconnect anytime; this just clears our pointer.")) return;
    setWorking(true);
    try {
      await api.connectDisconnect();
      toast.success("Disconnected. You can reconnect any time.");
      await load();
    } finally { setWorking(false); }
  };

  if (!me) return <div className="text-sm mono uppercase text-[#A1A1AA]">loading affiliate hub…</div>;

  const earnedTotal = ((me.lifetime_earnings_cents || 0) / 100).toFixed(2);
  const pending = ((me.pending_payout_cents || 0) / 100).toFixed(2);
  const paidOut = ((me.paid_out_cents || 0) / 100).toFixed(2);
  const t = me.current_tier;
  const next = me.next_tier;

  return (
    <div className="max-w-6xl space-y-8" data-testid="affiliate-dashboard">
      <div>
        <div className="text-eyebrow mb-2 flex items-center gap-2"><Award size={12}/> Affiliate hub</div>
        <h1 className="font-display font-black tracking-tighter text-5xl">Get paid <span className="text-[#F59E0B]">forever</span>.</h1>
        <p className="text-sm text-[#52525B] mt-3 max-w-2xl">Every customer you refer pays you {Math.round(me.rate * 100)}% of their plan every month they stay subscribed. For life. No caps. No tricks.</p>
      </div>

      {/* TIER + STATS */}
      <div className="grid md:grid-cols-4 gap-4" data-testid="affiliate-stats">
        <StatBig label="Current tier" value={t?.name || "Bronze"} sub={`${Math.round(t?.rate * 100)}% rate`} icon={Crown} />
        <StatBig label="Active referrals" value={me.active_referrals} sub={`${me.lead_referrals || 0} leads pending payment`} icon={TrendingUp} />
        <StatBig label="Lifetime earned" value={`$${earnedTotal}`} sub={`Paid out: $${paidOut}`} icon={DollarSign} />
        <StatBig label="Pending payout" value={`$${pending}`} sub="$100 min · 7 day turnaround" icon={Sparkles} pop />
      </div>

      {/* PROGRESS BAR TO NEXT TIER */}
      {next?.label !== "MAX" && (
        <div className="border border-black p-5">
          <div className="flex items-baseline justify-between mb-2">
            <div className="text-eyebrow">{next?.needed} more referrals to <strong className="text-black">{next?.label}</strong></div>
            <div className="text-eyebrow">Unlocks {Math.round((next?.rate || 0) * 100)}% rate</div>
          </div>
          <div className="h-3 bg-[#F4F5F6] border border-black overflow-hidden">
            <div className="h-full bg-[#F59E0B]" style={{ width: `${Math.min(100, (me.active_referrals / (me.active_referrals + next.needed)) * 100)}%` }}/>
          </div>
        </div>
      )}

      {/* SHARE LINK */}
      <div className="border-2 border-black p-6" data-testid="referral-link-block">
        <div className="text-eyebrow mb-2 flex items-center gap-2"><LinkIcon size={12}/> Your forever link</div>
        <div className="flex flex-wrap items-center gap-3">
          <code className="font-mono text-sm bg-[#FAFAFA] border border-[#E2E4E9] px-3 py-2 break-all flex-1 min-w-[280px]">{me.public_link}</code>
          <button onClick={copyLink} className="bg-black text-white px-5 py-2.5 text-sm flex items-center gap-2 hover:bg-[#002FA7]" data-testid="copy-link-button">
            <Copy size={14}/> Copy
          </button>
        </div>
        <p className="text-xs text-[#52525B] mt-3 leading-relaxed">Anyone clicking this link, then signing up with Google, gets attributed to you. You earn on every payment they make, forever.</p>
      </div>

      {/* SWIPE FILE WIZARD */}
      <div className="border border-black p-6" data-testid="swipe-files">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-eyebrow mb-1 flex items-center gap-2"><BookOpen size={12}/> Swipe wizard</div>
            <h3 className="font-display font-black text-2xl tracking-tighter">Plug-and-play copy that converts.</h3>
          </div>
          <button onClick={() => setShowSwipes(!showSwipes)} className="text-eyebrow hover:text-black">{showSwipes ? "Hide" : "Show"}</button>
        </div>
        {showSwipes && (
          <>
            <div className="flex flex-wrap gap-2 mb-4">
              {Object.keys(SWIPE_FILES).map((k) => (
                <button
                  key={k}
                  data-testid={`swipe-tab-${k}`}
                  onClick={() => setActiveSwipe(k)}
                  className={`text-sm px-3 py-1.5 border ${activeSwipe === k ? "bg-black text-white border-black" : "border-[#E2E4E9] hover:border-black"}`}
                >
                  {({ twitter: "X / Twitter", email_warm: "Warm Email", linkedin: "LinkedIn Post", dm: "DM" })[k]}
                </button>
              ))}
            </div>
            <pre className="bg-[#FAFAFA] border border-[#E2E4E9] p-4 text-sm whitespace-pre-wrap font-mono leading-relaxed">{SWIPE_FILES[activeSwipe]
              .replaceAll("{link}", me.public_link)
              .replaceAll("{brand_count}", me.active_referrals > 0 ? `${me.active_referrals + 1}` : "multiple")
              .replaceAll("{your_name}", (me.name || me.email || "").split(" ")[0] || "")}</pre>
            <button onClick={() => copySwipe(activeSwipe)} className="mt-3 bg-[#F59E0B] text-black px-5 py-2.5 text-sm flex items-center gap-2 hover:bg-black hover:text-white" data-testid={`copy-swipe-${activeSwipe}`}>
              <Copy size={14}/> Copy this swipe
            </button>
          </>
        )}
      </div>

      {/* PAYOUT — STRIPE CONNECT FIRST */}
      <div className="border-2 border-black p-6 space-y-5" data-testid="payout-block">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-eyebrow mb-1 flex items-center gap-2"><Banknote size={12}/> Payout</div>
            <h3 className="font-display font-black text-2xl tracking-tighter">Get paid automatically.</h3>
            <p className="text-sm text-[#52525B] mt-1 max-w-2xl">Connect your bank through Stripe once. Every commission ships on the schedule you choose. Money lands in your bank in 1-2 business days. We never see your bank details.</p>
          </div>
          {connectStatus?.payouts_enabled ? (
            <div className="flex items-center gap-2 border border-[#059669] bg-[#ECFDF5] px-3 py-2 text-sm" data-testid="connect-status-ok">
              <ShieldCheck size={14} className="text-[#059669]"/>
              <span className="font-semibold">Bank verified · Auto-payouts ON</span>
            </div>
          ) : connectStatus?.connected ? (
            <div className="flex items-center gap-2 border border-[#F59E0B] bg-[#FFFBEB] px-3 py-2 text-sm" data-testid="connect-status-pending">
              <AlertTriangle size={14} className="text-[#B45309]"/>
              <span>Stripe onboarding incomplete</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 border border-[#E2E4E9] bg-[#FAFAFA] px-3 py-2 text-sm" data-testid="connect-status-none">
              <span>Bank not connected yet</span>
            </div>
          )}
        </div>

        {/* CONNECT CTA */}
        {!connectStatus?.payouts_enabled && (
          <div className="border border-[#F59E0B] bg-[#FFFBEB] p-4 flex items-center justify-between gap-4 flex-wrap">
            <div className="text-sm max-w-xl">
              <div className="font-semibold mb-1">{connectStatus?.connected ? "Finish Stripe verification to auto-receive payouts" : "Connect Stripe to skip the manual payout queue"}</div>
              <div className="text-xs text-[#52525B] leading-relaxed">
                Without Stripe: payouts take 7 days, manual queue. With Stripe: instant transfers, you control the schedule (immediate / threshold / monthly). Stripe handles all the bank + tax (KYC) verification — we never see your numbers.
              </div>
            </div>
            <button
              onClick={() => setShowConnect(true)}
              disabled={working}
              data-testid="connect-stripe-button"
              className="bg-[#0A0A0B] text-white px-5 py-3 text-sm flex items-center gap-2 hover:bg-[#002FA7]"
            >
              <Banknote size={14}/> {connectStatus?.connected ? "Finish onboarding" : "Connect Stripe"}
            </button>
          </div>
        )}

        {/* AUTO-PAYOUT PREFERENCE — only when connected */}
        {connectStatus?.payouts_enabled && (
          <div className="border border-[#E2E4E9] p-4" data-testid="payout-prefs">
            <div className="text-eyebrow mb-3 flex items-center gap-2"><Settings size={12}/> Payout schedule</div>
            <div className="grid md:grid-cols-4 gap-2">
              {[
                { id: "immediate", label: "Immediate", icon: Zap, desc: "Money moves the moment a customer pays" },
                { id: "threshold", label: "Threshold", icon: DollarSign, desc: "Auto-payout when balance crosses threshold" },
                { id: "monthly", label: "Monthly batch", icon: Calendar, desc: "Single bulk payout on the 1st (cleanest)" },
                { id: "manual", label: "Manual", icon: Settings, desc: "You click 'Request payout' yourself" },
              ].map((p) => (
                <button
                  key={p.id}
                  onClick={() => { setPref(p.id); savePrefs(p.id, threshold); }}
                  data-testid={`pref-${p.id}`}
                  className={`text-left border-2 p-3 transition-colors ${pref === p.id ? "border-black bg-[#FAFAFA]" : "border-[#E2E4E9] hover:border-black"}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <p.icon size={14} />
                    <div className="font-semibold text-sm">{p.label}</div>
                  </div>
                  <div className="text-xs text-[#52525B] leading-snug">{p.desc}</div>
                </button>
              ))}
            </div>
            {pref === "threshold" && (
              <div className="mt-3 flex items-center gap-3 text-sm">
                <span className="text-eyebrow">Threshold:</span>
                <span className="mono">$</span>
                <input
                  type="number"
                  min={0}
                  step={5}
                  value={(threshold / 100)}
                  onChange={(e) => setThreshold(Math.max(0, Math.round(parseFloat(e.target.value || "0") * 100)))}
                  onBlur={() => savePrefs(pref, threshold)}
                  className="border-2 border-black px-2 py-1 text-sm w-24 mono"
                  data-testid="threshold-input"
                />
              </div>
            )}
            <div className="mt-3 text-xs mono text-[#A1A1AA]">
              Stripe Express transfer fee: ~0.25% + $0.25 per payout · deducted from your transferred amount.
            </div>
            <button
              onClick={disconnectStripe}
              className="mt-3 text-eyebrow text-[#A1A1AA] hover:text-[#E11D48]"
              data-testid="disconnect-stripe-button"
            >
              Disconnect Stripe (re-link any time)
            </button>
          </div>
        )}

        {/* MANUAL PAYOUT FALLBACK (legacy email) — only when NOT on Connect */}
        {!connectStatus?.payouts_enabled && (
          <div className="border border-[#E2E4E9] p-4">
            <div className="text-eyebrow mb-2 flex items-center gap-2"><Mail size={12}/> Or use legacy manual payout</div>
            <p className="text-xs text-[#52525B] mb-3">Slower (7-day manual), no schedule control. Use this only if you can't or won't use Stripe.</p>
            <div className="flex flex-wrap items-center gap-3">
              <input
                value={payoutEmail}
                onChange={(e) => setPayoutEmail(e.target.value)}
                placeholder="payout@yourdomain.com (PayPal/Wise)"
                className="border border-[#E2E4E9] px-3 py-2 text-sm min-w-[280px]"
                data-testid="payout-email-input"
              />
              <button onClick={savePayout} disabled={working} className="border border-black px-4 py-2 text-sm hover:bg-black hover:text-white" data-testid="save-payout-button">
                {payoutEmail === me.payout_email ? <><Check size={12} className="inline mr-1"/> Saved</> : "Save"}
              </button>
            </div>
          </div>
        )}

        {/* REQUEST PAYOUT BUTTON */}
        <div className="flex flex-wrap items-center gap-3">
          <button
            disabled={working || me.pending_payout_cents <= 0 || (!connectStatus?.payouts_enabled && !me.payout_email)}
            onClick={requestPayout}
            data-testid="request-payout-button"
            className="bg-[#F59E0B] text-black px-5 py-3 text-sm flex items-center gap-2 hover:bg-black hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <DollarSign size={14}/> {connectStatus?.payouts_enabled ? `Send $${pending} to my bank now` : `Request $${pending} payout`}
          </button>
          {me.pending_payout_cents <= 0 && (
            <div className="text-eyebrow text-[#A1A1AA]">Nothing pending</div>
          )}
        </div>
      </div>

      {/* REFERRAL LIST */}
      <div>
        <h3 className="font-display font-black text-2xl tracking-tighter mb-4">Your referrals</h3>
        {refs.length === 0 ? (
          <div className="border border-dashed border-[#E2E4E9] p-8 text-center text-sm text-[#52525B]">
            No referrals yet. Copy your link and post it.
          </div>
        ) : (
          <div className="border border-[#E2E4E9]" data-testid="referral-list">
            <div className="grid grid-cols-12 gap-3 px-4 py-3 text-eyebrow border-b border-[#E2E4E9] bg-[#FAFAFA]">
              <div className="col-span-3">Referred</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-3">First paid</div>
              <div className="col-span-2">Lifetime commission</div>
              <div className="col-span-2">Created</div>
            </div>
            {refs.map((r) => (
              <div key={r.referral_id} className="grid grid-cols-12 gap-3 px-4 py-3 text-sm border-b border-[#E2E4E9] last:border-0">
                <div className="col-span-3 mono">{r.referred_user_id.slice(-8)}</div>
                <div className="col-span-2">
                  <span className={`text-xs px-2 py-0.5 border ${r.status === "active" ? "bg-[#059669] text-white border-[#059669]" : "bg-white text-[#52525B] border-[#E2E4E9]"}`}>
                    {r.status}
                  </span>
                </div>
                <div className="col-span-3 mono text-xs">{r.first_paid_at ? r.first_paid_at.slice(0, 10) : "—"}</div>
                <div className="col-span-2 font-semibold">${(r.lifetime_commission_cents / 100).toFixed(2)}</div>
                <div className="col-span-2 mono text-xs">{r.created_at.slice(0, 10)}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* CREDITS LOG */}
      {credits.length > 0 && (
        <div>
          <h3 className="font-display font-black text-2xl tracking-tighter mb-4">Commission log</h3>
          <div className="border border-[#E2E4E9]">
            <div className="grid grid-cols-12 gap-3 px-4 py-3 text-eyebrow border-b border-[#E2E4E9] bg-[#FAFAFA]">
              <div className="col-span-4">Invoice</div>
              <div className="col-span-2">Customer paid</div>
              <div className="col-span-2">Your rate</div>
              <div className="col-span-2">Your cut</div>
              <div className="col-span-2">When</div>
            </div>
            {credits.slice(0, 20).map((c) => (
              <div key={c.credit_id} className="grid grid-cols-12 gap-3 px-4 py-3 text-sm border-b border-[#E2E4E9] last:border-0">
                <div className="col-span-4 mono text-xs truncate">{c.invoice_id || "—"}</div>
                <div className="col-span-2">${(c.gross_amount_cents / 100).toFixed(2)}</div>
                <div className="col-span-2 mono">{Math.round(c.rate * 100)}%</div>
                <div className="col-span-2 font-semibold text-[#059669]">${(c.commission_cents / 100).toFixed(2)}</div>
                <div className="col-span-2 mono text-xs">{c.created_at.slice(0, 10)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* INLINE STRIPE PAYOUTS + MANAGEMENT (embedded) */}
      {connectStatus?.payouts_enabled && (
        <ConnectInlinePanels />
      )}

      {/* MONTHLY CONTEST */}
      <MonthlyContest />

      <ConnectOnboardingModal
        open={showConnect}
        onClose={() => setShowConnect(false)}
        onComplete={() => { setShowConnect(false); load(); }}
      />
    </div>
  );
}

function MonthlyContest() {
  const [data, setData] = React.useState(null);
  React.useEffect(() => { api.affiliateContest().then(setData).catch(() => {}); }, []);
  if (!data) return null;

  return (
    <div className="border-2 border-black p-6 bg-[#FAFAFA]" data-testid="monthly-contest">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div>
          <div className="text-eyebrow flex items-center gap-2 mb-1"><Crown size={12}/> Monthly contest · {data.month}</div>
          <h3 className="font-display font-black text-3xl tracking-tighter">Top 3 every month win cash + perks.</h3>
        </div>
        {data.my_rank ? (
          <div className="bg-black text-white px-4 py-2 text-sm" data-testid="my-contest-rank">
            You're <span className="text-[#F59E0B] font-bold">#{data.my_rank.rank}</span> this month
          </div>
        ) : null}
      </div>

      <div className="grid md:grid-cols-3 gap-3 mb-5">
        {data.prizes.map((p) => (
          <div key={p.rank} className={`border-2 p-4 ${p.rank === 1 ? "border-[#F59E0B] bg-[#FFFBEB]" : "border-black bg-white"}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="text-eyebrow">#{p.rank}</div>
              <div className="font-display font-black text-2xl tracking-tighter">up to ${(p.cash_cap_cents / 100).toLocaleString()}</div>
            </div>
            <div className="text-xs text-[#52525B] leading-relaxed">{p.label}</div>
          </div>
        ))}
      </div>
      <div className="text-xs mono text-[#A1A1AA] mb-4">
        Prize = min(cap, 10% of your monthly commission earnings). Auto-paid via Stripe Connect on the 1st.
      </div>

      {data.top.length === 0 ? (
        <div className="border border-dashed border-[#E2E4E9] p-6 text-center text-sm text-[#52525B]">
          No commissions logged this month yet. Be the first.
        </div>
      ) : (
        <div className="border border-black bg-white">
          <div className="grid grid-cols-12 gap-3 px-4 py-2 text-eyebrow border-b border-black bg-[#FAFAFA]">
            <div className="col-span-1">#</div>
            <div className="col-span-4">Affiliate</div>
            <div className="col-span-2">Tier</div>
            <div className="col-span-2">Conversions</div>
            <div className="col-span-3 text-right">This month</div>
          </div>
          {data.top.map((r) => (
            <div key={r.rank} className={`grid grid-cols-12 gap-3 px-4 py-3 text-sm border-b border-[#E2E4E9] last:border-0 ${r.is_you ? "bg-[#FFFBEB]" : ""}`}>
              <div className="col-span-1 mono">{r.rank}</div>
              <div className="col-span-4 font-semibold flex items-center gap-2">
                {r.first_name} {r.is_you && <span className="text-[10px] mono text-[#B45309]">(you)</span>}
                {r.rank <= 3 && r.projected_prize_cents > 0 && (
                  <span className="text-[10px] mono px-1.5 py-0.5 bg-[#F59E0B] text-black">+${(r.projected_prize_cents / 100).toFixed(0)}</span>
                )}
              </div>
              <div className="col-span-2"><span className="text-xs px-2 py-0.5 border border-[#E2E4E9]">{r.tier}</span></div>
              <div className="col-span-2 mono">{r.wins}</div>
              <div className="col-span-3 text-right font-display font-bold">${(r.month_earnings_cents / 100).toFixed(2)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatBig({ label, value, sub, icon: Icon, pop }) {
  return (
    <div className={`border-2 ${pop ? "border-[#F59E0B] bg-[#FFFBEB]" : "border-black bg-white"} p-5`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-eyebrow">{label}</div>
        <Icon size={16} className={pop ? "text-[#B45309]" : ""}/>
      </div>
      <div className="font-display font-black text-3xl tracking-tighter">{value}</div>
      <div className="text-xs text-[#52525B] mt-1">{sub}</div>
    </div>
  );
}
